import React from 'react';
import { Divider, Grid, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOrderPage } from '../orderPageContext/useOrderPage';
import { useLanguage } from '../../utils/GeneralUtils';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { customerFacingPriceFormatter } from '../utils';
import { withBookingFee } from '../core/pricing';
import { PassOrderPageData } from '../../api';
import { isFixedScheduleMonthly } from '../../managePassesPage/utils/passUtils';
import PassTroubleshootAvailability from './PassTroubleshootAvailability';

const PassSummaryStep = () => {
  const { t } = useTranslation();
  const language = useLanguage();
  const theme = useTheme();
  const { orderPageState } = useOrderPage();
  const passOrderPage = orderPageState.orderPage as unknown as PassOrderPageData;
  const { account, specificOrderPage } = orderPageState;
  const troubleshoot = specificOrderPage?.troubleshoot === true;

  const redeemableEntries = (passOrderPage.redeemable_on || [])
    .map((item) => {
      const op = account?.experiences?.[0]?.order_pages?.find((p: any) => p.id === item.order_page_id);
      const name = op?.label?.[language] || '';
      return name ? { name, limit: item.limit } : null;
    })
    .filter(Boolean) as { name: string; limit?: number | null }[];

  return (
    <Grid container flexDirection="column">
      <Grid item mb={1}>
        <OrderPageTypography variant="h3" bold>
          {passOrderPage.label?.[language] || ''}
        </OrderPageTypography>
      </Grid>

      {!isFixedScheduleMonthly(passOrderPage.pass_type) && (
        <>
          <Grid container gap={2} mt={1} mb={1} justifyContent="center" flexWrap="nowrap">
            <Grid
              item
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 2,
                px: 1,
                borderRadius: 2,
                border: `1px solid ${theme.customTheme.palette.border || '#e0e0e0'}`,
              }}>
              <OrderPageTypography variant="body2">{t('pass.num_punches')}</OrderPageTypography>
              <OrderPageTypography variant="h3" bold color="button.outlined">
                {passOrderPage.num_punches ?? t('pass.unlimited')}
              </OrderPageTypography>
            </Grid>
            <Grid
              item
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 2,
                px: 1,
                borderRadius: 2,
                border: `1px solid ${theme.customTheme.palette.border || '#e0e0e0'}`,
              }}>
              <OrderPageTypography variant="body2">{t('pass.validity_days')}</OrderPageTypography>
              <OrderPageTypography variant="h3" bold color="button.outlined">
                {t('pass.days', { count: passOrderPage.validity_days })}
              </OrderPageTypography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
        </>
      )}

      <Grid container justifyContent="center" alignItems="center" gap={1}>
        <OrderPageTypography variant="h6">{t('pass.price')}</OrderPageTypography>
        <OrderPageTypography variant="h3" bold>
          {customerFacingPriceFormatter(withBookingFee(passOrderPage.price, account), account)}
          {isFixedScheduleMonthly(passOrderPage.pass_type) && ` ${t('manage_passes.buy.perMonth')}`}
        </OrderPageTypography>
      </Grid>

      {redeemableEntries.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <OrderPageTypography variant="h6" bold>
            {t('pass.redeemable_on')}
          </OrderPageTypography>
          <Grid component="ul" container flexDirection="column" sx={{ m: 0, mt: 1, pl: 2.5 }}>
            {redeemableEntries.map(({ name, limit }) => (
              <Grid
                component="li"
                item
                key={name}
                sx={{ py: 0.25, '&::marker': { color: theme.customTheme.palette.primary } }}>
                <OrderPageTypography variant="body1">
                  {name}
                  {limit != null && (
                    <span style={{ opacity: 0.6, fontSize: '0.875em' }}>
                      {' '}
                      {t('pass.punch_limit', { count: limit })}
                    </span>
                  )}
                </OrderPageTypography>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {troubleshoot && <PassTroubleshootAvailability passOrderPage={passOrderPage} account={account} />}
    </Grid>
  );
};

export default PassSummaryStep;
